import React, { useRef, useCallback, useState } from 'react';
import { gsap } from 'gsap';
import { halfSliderContent } from './HalfSlider.content';
import styles from './HalfSlider.module.sass';
import cloud from './assets/cloud.webp';
import arrow from './assets/arrow.svg';

const DURATION = 0.7;
const EASE_OUT = 'power3.out';
const EASE_IN = 'power2.in';
const STAGGER = 0.08;
const SWIPE_MIN_PX = 48;
const SWIPE_DOMINANCE = 1.15;

export function HalfSlider() {
  const { slides } = halfSliderContent;
  const n = slides.length;
  const [current, setCurrent] = useState(0);
  const animatingRef = useRef(false);
  const touchStartRef = useRef(null);

  const titleLineRefs = useRef([]);
  const descRef = useRef(null);
  const thumbRefs = useRef([]);
  const photoRefs = useRef([]);

  const setTitleLineRef = useCallback((el, i) => {
    titleLineRefs.current[i] = el;
  }, []);

  const setThumbRef = useCallback((el, i) => {
    thumbRefs.current[i] = el;
  }, []);

  const setPhotoRef = useCallback((el, i) => {
    photoRefs.current[i] = el;
  }, []);

  const goTo = useCallback(
    (next) => {
      if (animatingRef.current || next === current) return;
      animatingRef.current = true;

      const direction = next > current ? 1 : -1;
      const tl = gsap.timeline({
        onComplete: () => {
          animatingRef.current = false;
          setCurrent(next);
        },
      });

      const titleInners = titleLineRefs.current
        .filter(Boolean)
        .map((el) => el.querySelector(`.${styles.halfSlider__titleInner}`));

      tl.to(
        titleInners,
        {
          yPercent: direction * -110,
          duration: DURATION * 0.5,
          ease: EASE_IN,
          stagger: STAGGER,
        },
        0,
      );

      if (descRef.current) {
        tl.to(
          descRef.current,
          {
            opacity: 0,
            y: direction * -20,
            duration: DURATION * 0.4,
            ease: EASE_IN,
          },
          0,
        );
      }

      const currentThumb = thumbRefs.current[current];
      const nextThumb = thumbRefs.current[next];

      if (currentThumb) {
        tl.to(
          currentThumb,
          {
            clipPath: direction > 0
              ? 'inset(0 100% 0 0)'
              : 'inset(0 0 0 100%)',
            scale: 1.15,
            duration: DURATION * 0.6,
            ease: EASE_IN,
          },
          0,
        );
      }

      const currentPhoto = photoRefs.current[current];
      const nextPhoto = photoRefs.current[next];

      if (currentPhoto) {
        tl.to(
          currentPhoto,
          {
            clipPath: direction > 0
              ? 'inset(0 100% 0 0)'
              : 'inset(0 0 0 100%)',
            duration: DURATION,
            ease: 'power2.inOut',
          },
          0.1,
        );
      }

      if (nextPhoto) {
        gsap.set(nextPhoto, {
          clipPath: direction > 0
            ? 'inset(0 0 0 100%)'
            : 'inset(0 100% 0 0)',
          scale: 1.08,
        });
        tl.to(
          nextPhoto,
          {
            clipPath: 'inset(0 0% 0 0%)',
            scale: 1,
            duration: DURATION * 1.2,
            ease: EASE_OUT,
          },
          DURATION * 0.3,
        );
      }

      if (nextThumb) {
        gsap.set(nextThumb, {
          clipPath: direction > 0
            ? 'inset(0 0 0 100%)'
            : 'inset(0 100% 0 0)',
          scale: 0.85,
        });
        tl.to(
          nextThumb,
          {
            clipPath: 'inset(0 0% 0 0%)',
            scale: 1,
            duration: DURATION * 0.8,
            ease: EASE_OUT,
          },
          DURATION * 0.4,
        );
      }

      tl.call(
        () => {
          const slide = slides[next];
          titleInners.forEach((el, i) => {
            if (!el) return;
            const line = slide.title[i];
            if (line !== undefined) {
              el.textContent = line;
            }
          });
          if (descRef.current) {
            descRef.current.innerHTML = `<span class="${styles.halfSlider__descAccent}">${slide.descriptionAccent}</span> ${slide.description}`;
          }
        },
        null,
        DURATION * 0.55,
      );

      tl.fromTo(
        titleInners,
        {
          yPercent: direction * 110,
        },
        {
          yPercent: 0,
          duration: DURATION * 0.6,
          ease: EASE_OUT,
          stagger: STAGGER,
        },
        DURATION * 0.55,
      );

      if (descRef.current) {
        tl.fromTo(
          descRef.current,
          {
            opacity: 0,
            y: direction * 30,
          },
          {
            opacity: 1,
            y: 0,
            duration: DURATION * 0.6,
            ease: EASE_OUT,
          },
          DURATION * 0.65,
        );
      }
    },
    [current, slides],
  );

  const goPrev = useCallback(() => {
    goTo((current - 1 + n) % n);
  }, [current, n, goTo]);

  const goNext = useCallback(() => {
    goTo((current + 1) % n);
  }, [current, n, goTo]);

  const onTouchStart = useCallback((e) => {
    const t = e.touches[0];
    if (!t) return;
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  }, []);

  const onTouchEnd = useCallback(
    (e) => {
      const start = touchStartRef.current;
      touchStartRef.current = null;
      if (!start) return;
      const t = e.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;
      if (Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) < Math.abs(dy) * SWIPE_DOMINANCE) {
        return;
      }
      if (dx < 0) goNext();
      else goPrev();
    },
    [goNext, goPrev]
  );

  const currentSlide = slides[current];
  const maxTitleLines = Math.max(...slides.map((s) => s.title.length));

  return (
    <section className={styles.halfSlider} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <img src={cloud} alt="" className={styles.halfSlider__cloud} />
      <div className={styles.halfSlider__left}>
        <div className={styles.halfSlider__titleWrap}>
          <h2 className={styles.halfSlider__title}>
            {Array.from({ length: maxTitleLines }).map((_, i) => (
              <span
                key={i}
                ref={(el) => setTitleLineRef(el, i)}
                className={styles.halfSlider__titleLine}
              >
                <span
                  className={`${styles.halfSlider__titleInner} ${
                    i === 0 ? styles.halfSlider__titleLineAccent : styles.halfSlider__titleLineMuted
                  }`}
                >
                  {currentSlide.title[i] || ''}
                </span>
              </span>
            ))}
          </h2>

          <div className={styles.halfSlider__nav}>
            <button
              className={styles.halfSlider__navBtn}
              onClick={goPrev}
              aria-label="Предыдущий слайд"
            >
              <img src={arrow} alt="" className={styles.halfSlider__navBtnIcon} />
            </button>
            <button
              className={styles.halfSlider__navBtn}
              onClick={goNext}
              aria-label="Следующий слайд"
            >
              <img
                src={arrow}
                alt=""
                className={styles.halfSlider__navBtnIcon + ' ' + styles.halfSlider__navBtnIconRight}
              />
            </button>
          </div>
        </div>

        <div className={styles.halfSlider__thumbWrap}>
          <div className={styles.halfSlider__thumbInner}>
            {slides.map((slide, i) => (
              <img
                key={i}
                ref={(el) => setThumbRef(el, i)}
                src={slide.thumbnail}
                alt=""
                className={styles.halfSlider__thumbImg}
                style={{
                  clipPath: i === 0 ? 'inset(0)' : 'inset(0 100% 0 0)',
                  zIndex: i === 0 ? 2 : 1,
                }}
                decoding="async"
              />
            ))}
          </div>
        </div>

        <div className={styles.halfSlider__bottom}>
          <p ref={descRef} className={styles.halfSlider__desc}>
            <span className={styles.halfSlider__descAccent}>{currentSlide.descriptionAccent}</span>{' '}
            {currentSlide.description}
          </p>
        </div>
      </div>

      <div className={styles.halfSlider__right}>
        <div className={styles.halfSlider__photoInner}>
          {slides.map((slide, i) => (
            <img
              key={i}
              ref={(el) => setPhotoRef(el, i)}
              src={slide.photo}
              alt=""
              className={styles.halfSlider__photoImg}
              style={{
                clipPath: i === 0 ? 'inset(0)' : 'inset(0 100% 0 0)',
                zIndex: i === 0 ? 2 : 1,
              }}
              decoding="async"
            />
          ))}
        </div>
      </div>
    </section>
  );
}
